// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import { color, mergeWithCss, parseBoolean } from "@stanion/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    text?: string;
    customWidth?: string;
    extendedStyles?: TypographyPresentationProps;
}

const borderWidth = "1px";
const edgeSize = "9px";

const productPreviousPurchaseStyles: TypographyPresentationProps = {
    size: 10,
    weight: "bold",
    color: "text.main",
    css: css`
        ${({ customWidth }: any) => css`
            ${customWidth !== undefined &&
            css`
                width: ${customWidth};
            `};
        `}
        background: ${color("secondary")};
        padding: 5px 9px;
        border: 0;
        position: relative;
        isolation: isolate;
        margin-top: 8px;
        display: inline-block;
        text-align: center;
        -webkit-clip-path: polygon(
            ${edgeSize} 0%,
            100% 0,
            100% calc(100% - ${edgeSize}),
            calc(100% - ${edgeSize}) 100%,
            0 100%,
            0% ${edgeSize}
        );
        clip-path: polygon(
            ${edgeSize} 0%,
            100% 0,
            100% calc(100% - ${edgeSize}),
            calc(100% - ${edgeSize}) 100%,
            0 100%,
            0% ${edgeSize}
        );

        &::before {
            content: "";
            position: absolute;
            inset: 0;
            background: ${color("primary")};
            z-index: -2;
        }

        &::after {
            content: "";
            position: absolute;
            inset: 0;
            background: ${color("secondary")};
            z-index: -1;
            -webkit-clip-path: polygon(
                ${borderWidth} calc(${edgeSize} + ${borderWidth} * 0.5),
                calc(${edgeSize} + ${borderWidth} * 0.5) ${borderWidth},
                calc(100% - ${borderWidth}) ${borderWidth},
                calc(100% - ${borderWidth}) calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)),
                calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)) calc(100% - ${borderWidth}),
                calc(${borderWidth}) calc(100% - ${borderWidth})
            );
            clip-path: polygon(
                ${borderWidth} calc(${edgeSize} + ${borderWidth} * 0.5),
                calc(${edgeSize} + ${borderWidth} * 0.5) ${borderWidth},
                calc(100% - ${borderWidth}) ${borderWidth},
                calc(100% - ${borderWidth}) calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)),
                calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)) calc(100% - ${borderWidth}),
                calc(${borderWidth}) calc(100% - ${borderWidth})
            );
        }
    `,
};

const ProductPreviousPurchase: FC<OwnProps> = ({ product, text, extendedStyles, customWidth }) => {
    const [styles] = useState(() => mergeWithCss(productPreviousPurchaseStyles, extendedStyles));

    if (!parseBoolean(product.properties?.previousPurchased)) {
        return null;
    }

    return (
        <Typography {...styles} data-test-selector="ProductPreviousPurchase" {...{ customWidth }}>
            {text || translate("Prev. Purchase")}
        </Typography>
    );
};

export default ProductPreviousPurchase;
